import React from "react";
import { ArrowRight, Check } from "lucide-react";

export type TypeShowcaseProps = {
  eyebrow: string;
  name: string;
  color: string;
  background: string;
  image: string;
  imageSide: "left" | "right";
  /** Desktop photo height from the design (420 on Work, 440 on Stay) */
  imageHeight?: number;
  /** Bottom-up gradient over the photo; only where the design shows one */
  gradient?: "none" | "soft" | "strong";
  /** Strip pinned to the bottom of the photo */
  imageFooter: React.ReactNode;
  description: string;
  included: string[];
  whyItWorks: string[];
  cta: string;
  onCta: () => void;
  className?: string;
  /** Hex alpha for the check-mark circles: "14" (8%) on Work, "1a" (10%) on Stay */
  checkTint?: string;
  /** Play uses a shorter strip that leaves the bottom-right corner free */
  compactFooter?: boolean;
  /** Small inset photo in the bottom-right corner of the main photo (Play Dining) */
  detailImage?: string;
};

/** Photo + "What's included" / "Why it works" block used for each category type. */
export default function TypeShowcase({
  eyebrow,
  name,
  color,
  background,
  image,
  imageSide,
  imageHeight = 420,
  gradient = "none",
  imageFooter,
  description,
  included,
  whyItWorks,
  cta,
  onCta,
  className = "py-20",
  checkTint = "14",
  compactFooter = false,
  detailImage,
}: TypeShowcaseProps) {
  const photo = (
    <div
      className="relative h-[320px] overflow-hidden rounded-3xl sm:h-[var(--photo-height)]"
      style={{ "--photo-height": `${imageHeight}px` } as React.CSSProperties}
    >
      <img src={image} alt={name} className="absolute inset-0 size-full object-cover" />
      {gradient === "soft" && <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />}
      {gradient === "strong" && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent via-50% to-transparent" />
      )}
      <span
        className="absolute left-[20px] top-[20px] rounded-full px-[12px] py-[5px] text-[12px] font-bold uppercase leading-4 tracking-[1.2px] text-white"
        style={{ backgroundColor: color }}
      >
        {name}
      </span>
      {detailImage && (
        <img
          src={detailImage}
          alt=""
          className="absolute bottom-[20px] right-[20px] hidden size-[112px] rounded-2xl border-2 border-solid border-white/30 object-cover shadow-lg sm:block"
        />
      )}
      <div
        className={`absolute bottom-[20px] left-[20px] border-[1px] border-solid border-white/60 bg-white/95 shadow-[0_4px_6px_0_rgba(0,0,0,0.1),0_2px_4px_0_rgba(0,0,0,0.1)] ${
          compactFooter
            ? "right-[20px] rounded-[14px] px-[12px] py-[10px] sm:right-auto sm:w-[calc(100%-172px)] sm:max-w-[500px]"
            : "right-[20px] rounded-2xl px-[16px] py-[12px]"
        }`}
      >
        {imageFooter}
      </div>
    </div>
  );

  const text = (
    <div>
      <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px]" style={{ color }}>
        {eyebrow}
      </p>
      <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
        {name}
      </h2>
      <p className="pt-[16px] text-[16px] leading-[26px] text-[#717182]">{description}</p>

      <p className="pt-8 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#0a0a0a]">
        What's included
      </p>
      <ul className="space-y-2.5 pt-[16px]">
        {included.map(item => (
          <li key={item} className="flex items-start gap-2.5 text-[14px] leading-5 text-[#3d3d4d]">
            <span
              className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: `${color}${checkTint}` }}
            >
              <Check className="size-2.5" style={{ color }} />
            </span>
            {item}
          </li>
        ))}
      </ul>

      <div className="my-8 rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-[#f5f8ff] p-[20px]">
        <p className="text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#0a0a0a]">Why it works</p>
        <ul className="space-y-2 pt-[12px]">
          {whyItWorks.map(item => (
            <li key={item} className="flex items-start gap-2 text-[14px] leading-5 text-[#717182]">
              <ArrowRight className="mt-0.5 size-[13px] shrink-0" style={{ color }} />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        onClick={onCta}
        className="inline-flex items-center gap-2 text-[14px] font-semibold leading-5 hover:underline"
        style={{ color }}
      >
        {cta}
        <ArrowRight className="size-[15px]" />
      </button>
    </div>
  );

  return (
    <section className={`${background} ${className}`}>
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-[16px] sm:px-8 lg:grid-cols-2">
        {/* Photo first on mobile; on desktop it alternates sides */}
        <div className={imageSide === "right" ? "lg:col-start-2 lg:row-start-1" : ""}>{photo}</div>
        <div className={imageSide === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>{text}</div>
      </div>
    </section>
  );
}
